import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { DEFAULT_WA_MESSAGES } from "@/config/site";
import { Activity, HeartPulse, Zap, Users, Wifi, ClipboardCheck, ArrowRight, Check, Timer, Sparkles } from "lucide-react";
import { usePageCms } from "@/lib/siteSettings";

const SERVICES = [
  {
    icon: HeartPulse,
    title: "Sport therapy",
    tag: "In-person / remote",
    href: "/prices#cat-sport-therapy",
    desc: "Hands-on assessment, manual therapy and progressive exercise to get you moving without pain.",
    bullets: ["Injury diagnosis", "Manual therapy", "Progressive rehab", "Return-to-activity planning"],
  },
  {
    icon: Zap,
    title: "Strength & conditioning",
    tag: "Performance",
    href: "/prices#cat-strength-conditioning",
    desc: "Individualised programming — from beginner to elite. Get stronger, move better, perform higher.",
    bullets: ["1:1 coaching", "Bespoke programming", "Force-plate testing", "Season periodisation"],
  },
  {
    icon: Sparkles,
    title: "Massage",
    tag: "Recovery",
    href: "/prices#cat-massage",
    desc: "Sports massage, deep tissue and myofascial release — hands-on recovery treatments.",
    bullets: ["Sports massage", "Deep tissue", "Myofascial release", "30 or 60 min sessions"],
  },
  {
    icon: ClipboardCheck,
    title: "Return-to-sport testing",
    tag: "Battery",
    href: "/prices#cat-physical-test",
    desc: "Objective battery of tests to make sure you're truly ready. Not guesswork — numbers.",
    bullets: ["Dynamometer strength", "Hop symmetry (LSI)", "CMJ height", "Psych readiness score"],
  },
  {
    icon: Wifi,
    title: "Remote monitoring",
    tag: "Hybrid care",
    href: "/prices#cat-remote-services",
    desc: "Between sessions, we track your check-ins, wearable data and exercise logs — and adjust the plan.",
    bullets: ["Daily check-ins", "Weekly progress summary", "Wearable sync", "Direct message clinician"],
  },
  {
    icon: Users,
    title: "Team & club care",
    tag: "Organisations",
    href: "/contact",
    desc: "Bespoke care packages for sports teams, clubs and organisations — pitch-side and in-clinic.",
    bullets: ["Multi-athlete dashboard", "Injury surveillance", "Pre-season screening", "On-site clinic days"],
  },
  {
    icon: Activity,
    title: "Post-op rehabilitation",
    tag: "Structured pathway",
    href: "/prices",
    desc: "Structured, milestone-based recovery after ACL, meniscus, shoulder and other orthopaedic surgeries.",
    bullets: ["Surgeon-aligned protocols", "Weekly milestone reviews", "Objective progression gates", "RTS clearance battery"],
  },
  {
    icon: ClipboardCheck,
    title: "Online programmes",
    tag: "6-week self-guided",
    href: "/prices#online-programmes",
    desc: "Structured 6-week rehab plans you can follow at your own pace — Back, Knee and Shoulder Rebuild.",
    bullets: ["6 weeks · progressive loading", "Video demos + printable PDF", "Screened for suitability first", "£119 one-off · UK-only"],
  },
];

const PATHWAY = [
  { step: "01", title: "Book", body: "60-min initial assessment — in-person or remote. We listen first." },
  { step: "02", title: "Assess", body: "Deep-dive movement screen + objective baseline testing." },
  { step: "03", title: "Build", body: "A personalised plan in the app — exercises, videos, milestones." },
  { step: "04", title: "Progress", body: "Weekly reviews, wearable sync, and re-testing to prove change." },
];

export default function Services() {
  const { t } = useTranslation();
  const cms = usePageCms("services");
  // Every book/CTA on this page funnels into the /prices flow — patient picks
  // a service card → lands on the matching tab → clicks Buy → CheckoutSummary
  // auto-detects logged-out and routes through /register?redirect=<summary_url>
  // → back to CheckoutSummary → pay. So every button here just needs to point
  // at /prices (or the anchored tab); the checkout flow owns the rest.
  const bookHref = "/prices";
  return (
    <div className="min-h-screen fl-noise fl-page" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />

      {/* Hero */}
      {cms.isVisible("hero") && (
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 px-6 bg-[#0A0A0A] fl-noise relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="fl-label text-[color:var(--fl-orange)] mb-4">{cms.text("hero", "eyebrow", t("servicesPage.eyebrow"))}</div>
          <h1 className="fl-heading text-5xl md:text-6xl leading-none">
            {cms.get("hero").heading ? cms.get("hero").heading : (<>{t("servicesPage.h1a")}<br /><span className="text-[color:var(--fl-orange)]">{t("servicesPage.h1b")}</span></>)}
          </h1>
          <p className="mt-6 text-lg max-w-2xl mx-auto" style={{ color: "var(--fl-muted)" }}>
            {cms.text("hero", "body", t("servicesPage.subtitle"))}
          </p>
        </div>
      </section>
      )}

      {/* Services grid */}
      <section className="py-24 md:py-32 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-5 fl-stagger">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                to={s.href}
                key={s.title}
                className="fl-card p-6 h-full flex flex-col hover:border-[#f47a20]/50 hover:-translate-y-0.5 transition-all group"
                data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <Icon size={24} className="text-[color:var(--fl-orange)]" />
                  <span className="fl-label">{s.tag}</span>
                </div>
                <h3 className="fl-heading text-xl">{s.title}</h3>
                <p className="mt-2 text-sm" style={{ color: "var(--fl-muted)" }}>{s.desc}</p>
                <ul className="mt-4 space-y-2 text-sm flex-1">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check size={14} className="mt-0.5 text-[color:var(--fl-orange)] flex-none" />
                      <span style={{ color: "var(--fl-text-2)" }}>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-3 border-t border-white/5 text-xs text-[#f47a20] flex items-center gap-1 group-hover:gap-2 transition-all">
                  Book <ArrowRight size={11} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Pathway */}
      {cms.isVisible("pathway") && (
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("pathway", "eyebrow", t("servicesPage.pathwayEyebrow"))}</div>
            <h2 className="fl-heading text-4xl md:text-5xl">{cms.text("pathway", "heading", t("servicesPage.pathwayTitle"))}</h2>
            <p className="mt-4 max-w-xl mx-auto text-sm" style={{ color: "var(--fl-muted)" }}>
              {cms.text("pathway", "body", t("servicesPage.pathwaySubtitle"))}
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-4 fl-stagger">
            {PATHWAY.map((p) => (
              <div key={p.step} className="fl-card p-6">
                <div className="fl-heading text-4xl text-[color:var(--fl-orange)]">{p.step}</div>
                <div className="fl-heading text-lg mt-4">{p.title}</div>
                <p className="mt-2 text-sm" style={{ color: "var(--fl-muted)" }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Our philosophy — moved here from the home page (Feb 2026). Uses the
          shared `pub.land.phil*` keys so no translation duplication is needed. */}
      {cms.isVisible("philosophy") && (
      <section className="py-24 md:py-32 px-6 bg-[#1A1A1A] border-t border-white/5" data-testid="services-philosophy">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("philosophy", "eyebrow", t("pub.land.philEyebrow"))}</div>
            <h2 data-reveal className="fl-heading text-4xl md:text-5xl leading-tight">
              {cms.get("philosophy").heading ? cms.get("philosophy").heading : (<>{t("pub.land.philH1a")}<br />{t("pub.land.philH1b")}</>)}
            </h2>
          </div>
          <div className="md:col-span-8">
            <div className="grid md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <div className="fl-label text-[color:var(--fl-orange)]">0{i}</div>
                  <div className="fl-heading text-2xl mt-2">{t(`pub.land.p${i}t`)}</div>
                  <div className="text-sm mt-2 leading-relaxed" style={{ color: "var(--fl-muted)" }}>
                    {t(`pub.land.p${i}d`)}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-10 text-lg leading-relaxed border-l-2 border-[color:var(--fl-orange)] pl-6 italic" style={{ color: "var(--fl-text-2, var(--fl-text))" }}>
              "{t("pub.land.philQuote")}"
            </p>
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      {cms.isVisible("cta") && (
      <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-[#0A0A0A] via-[#170f0d] to-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto fl-card p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 100%, rgba(255,69,0,0.10), transparent 60%)" }} />
          <div className="relative">
            <div className="flex items-center justify-center gap-2 fl-label text-[color:var(--fl-orange)] mb-3">
              <Timer size={12} /> {cms.text("cta", "eyebrow", t("servicesPage.firstIn48"))}
            </div>
            <h3 className="fl-heading text-3xl md:text-4xl">{cms.text("cta", "heading", t("servicesPage.ctaTitle"))}</h3>
            <p className="mt-3 max-w-xl mx-auto text-sm" style={{ color: "var(--fl-muted)" }}>
              {cms.text("cta", "body", t("servicesPage.ctaBody"))}
            </p>
            <Link to={bookHref} className="fl-btn text-base mt-6" data-testid="services-cta-book">
              {cms.text("cta", "ctaLabel", t("servicesPage.ctaBook"))} <ArrowRight size={16} />
            </Link>
            <div className="mt-4">
              <WhatsAppCTA message={DEFAULT_WA_MESSAGES.finder} variant="soft" testid="services-cta-wa">
                {t("pub.wa.needHelp")}
              </WhatsAppCTA>
            </div>
          </div>
        </div>
      </section>
      )}

      <PublicFooter />
    </div>
  );
}
